/**
 * 1.7.0 stream B (docs/dev/formats-1.7.0-build.md): Side by side: every format playing next to the others, in sync, at
 * one shared height that fits the monitor. The active format is the monitor's own DOM stage (ProgramViewer places it in
 * the row by its order); every other format is a panel here, drawn on a canvas from the monitor's own media elements
 * (one decode, so nothing drifts), each with its own crop windows, layers and Blur fit (monitor-paint.ts), its own
 * graphics frame (one OGraf instance per format, in that format's layout) and its own subtitle line.
 *
 * The canvases are drawn on every frame of playback through the monitor's graphics clock, when the paused playhead or
 * the document changes, and when an element has a new frame (seeked, loaded): no React render per frame. Paused, a
 * panel keeps its last frame while the elements seek, so scrubbing does not flicker (1.7.1, monitor-paint frameReady).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { documentForFormat, storylinePictureFades, type FormatRef, type GraphicTemplateDTO, type ResolvedFormat, type SequenceDocument, type Size } from '@prism/contracts';
import { CueOverlay } from '../../subtitles/CueOverlay';
import { paintFormat, type PictureElement } from './monitor-paint';
import { formatColor } from './formatContext';

export interface SideBySideProps {
  doc: SequenceDocument;
  formats: ResolvedFormat[];
  /** Sequence ticks. */
  playhead: number;
  playing: boolean;
  /** The monitor's picture elements by clip id, to draw the other formats from (stream B: images too). */
  videos: () => Map<string, PictureElement>;
  /** Displayed picture sizes of the sources, by version id. */
  sourceSizes: Map<string, Size>;
  templates?: GraphicTemplateDTO[];
  /** Added by stream B: the format shown on the monitor's own stage (no panel here). */
  activeId: FormatRef;
  /** Added by stream B: each format's panel size in CSS pixels, in `formats` order (monitor-geometry sideBySideSizes). */
  sizes: Size[];
  /** Added by stream B: the monitor's per-frame playhead listeners during playback (graphicsClock). */
  clock: Set<(ph: number) => void>;
  /** Added by stream B: called when a media element has a new frame to draw (seeked, loaded) while paused. */
  frames: Set<() => void>;
  /** Added by stream B: the graphics of a format's document (the monitor's graphics layers). */
  graphics?: (doc: SequenceDocument) => ReactNode;
  /** 1.7.1: the safe areas on every panel (the monitor's Safe areas button). */
  safeArea?: boolean;
}

function Panel({ format, fdoc, size, playhead, playing, videos, clock, frames, graphics, safeArea }: {
  format: ResolvedFormat; fdoc: SequenceDocument; size: Size; playhead: number; playing: boolean;
  videos: () => Map<string, PictureElement>; clock: Set<(ph: number) => void>; frames: Set<() => void>; graphics?: (doc: SequenceDocument) => ReactNode;
  safeArea?: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const vfades = useMemo(() => storylinePictureFades(fdoc), [fdoc]);
  const ph = useRef(playhead);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const draw = useCallback((at: number) => {
    ph.current = at;
    const c = canvas.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const map = videos();
    // Paused, nothing is painted until the frame's elements have settled (1.7.1); onReady below paints it then.
    paintFormat(ctx, fdoc, at, c.width, c.height, (id) => map.get(id), { playing: playingRef.current, vfades });
  }, [fdoc, videos, vfades]);
  const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  const pw = Math.max(1, Math.round(size.width * dpr)), phh = Math.max(1, Math.round(size.height * dpr));
  useLayoutEffect(() => { if (!playing) draw(playhead); }, [draw, playhead, playing, pw, phh]);
  useEffect(() => {
    const onFrame = (t: number) => draw(t);
    const onReady = () => { if (!playingRef.current) draw(ph.current); };
    clock.add(onFrame);
    frames.add(onReady);
    return () => { clock.delete(onFrame); frames.delete(onReady); };
  }, [clock, frames, draw]);
  return (
    <div className="sbs-panel" style={{ order: format.index, width: size.width, height: size.height, ['--chip' as string]: formatColor(format.index) }}
      data-testid="sbs-panel" data-format={format.id}>
      <canvas ref={canvas} width={pw} height={phh} className="sbs-canvas" data-testid={`sbs-canvas-${format.id}`} />
      <div className="viewer-overlays">
        {graphics?.(fdoc)}
        {safeArea && <div className="safe-area" data-testid="safe-area" aria-hidden><div className="safe-action" /><div className="safe-title" /></div>}
      </div>
      {fdoc.subtitles && <CueOverlay cues={fdoc.subtitles.cues} trackStyle={fdoc.subtitles.style} ticks={playhead} rect={{ left: 0, top: 0, width: size.width, height: size.height }} subtitleBottom={fdoc.subtitleBottom} testId="sbs-subtitle" />}
      <span className="sbs-label">{format.label}</span>
    </div>
  );
}

export function SideBySide({ doc, formats, playhead, playing, videos, activeId, sizes, clock, frames, graphics, safeArea }: SideBySideProps) {
  const docs = useMemo(() => new Map(formats.map((f) => [f.id, documentForFormat(doc, f.id)] as const)), [doc, formats]);
  return (
    <div className="side-by-side" data-testid="side-by-side" data-formats={formats.length}>
      {formats.map((f, i) => f.id === activeId || !sizes[i] ? null : (
        <Panel key={f.id} format={f} fdoc={docs.get(f.id)!} size={sizes[i]!} playhead={playhead} playing={playing} videos={videos} clock={clock} frames={frames} graphics={graphics} safeArea={safeArea} />
      ))}
    </div>
  );
}
